import React, { useState } from "react";
import { Card, CardBody, Row, Col, Modal } from "reactstrap";
import ViewUploadedID from "modals/ViewUploadedID";
import constants from "constants";
import { useTranslation } from "context/TranslationProvider";

const parseSaved = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const SenderDetails = ({ senderDetails, offerDetails, fallbackPickup }) => {
  const { t } = useTranslation();

  const [modal, setModal] = useState(false);
  const [idUrl, setIdUrl] = useState(null);

  const toggleModal = () => setModal((prev) => !prev);

  const zoomImage = (url) => {
    setIdUrl(url);
    setModal(true);
  };

  const packageInfo = senderDetails?.packageDetails || offerDetails || {};
  const pickup = senderDetails?.pickUpDetails ?? fallbackPickup ?? {};

  const items = parseSaved(packageInfo.items);

  const getPackageImageUrl = (image) =>
    `${constants.s3Bucket}/package-images/${image}`;

  const getItemImageUrl = (image) =>
    `${constants.s3Bucket}/item-images/${image}`;

  const getItemImages = (item) => {
    if (Array.isArray(item?.images)) return item.images;
    if (typeof item?.images === "string") {
      try {
        const parsed = JSON.parse(item.images);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        return item.images.split(",").map((s) => s.trim()).filter(Boolean);
      }
    }
    if (Array.isArray(item?.image)) return item.image;
    if (typeof item?.image === "string" && item.image) return [item.image];
    return [];
  };

  return (
    <>
      <h3 className="mb-3 font-weight-bold heading-lg">
        {t("page.orders.sender", "Sender")}
      </h3>

      <Row className="gx-3">
        <Col md="6" className="d-flex flex-column">
          <Card className="card-profile shadow border-0 mb-3 flex-grow-1">
            <CardBody className="py-2 px-3 h-100">
              <h4 className="heading-md text-muted mb-2">
                {t("page.orders.packageDetails.title", "Package Details")}
              </h4>

              <p className="heading-sm mb-0">
                <strong>{t("page.orders.packageDetails.weight", "Weight")}:</strong>{" "}
                {packageInfo.weight || offerDetails?.weight || t("page.orders.packageDetails.noItems", "N/A")}
              </p>

              <p className="heading-sm mb-1">
                <strong>{t("page.orders.baggageDetails.size", "Size")}:</strong>{" "}
                {packageInfo.size || offerDetails?.size || t("page.orders.packageDetails.noItems", "N/A")}
              </p>

              {items.length > 0 ? (
                <div className="mt-2 mb-1">
                  <p className="heading-sm mb-1">
                    <strong>{t("page.orders.packageDetails.itemDetails", "Item Details")}:</strong>
                  </p>
                  <div className="d-flex flex-column" style={{ gap: "6px" }}>
                    {items.map((item, i) => {
                      const itemImages = getItemImages(item);
                      return (
                        <div
                          key={i}
                          className="d-flex flex-column"
                          style={{
                            background: "#FAFAFA",
                            border: "1px solid #EEE",
                            borderRadius: "8px",
                            padding: "8px 12px",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <p className="mb-0 heading-sm" style={{ fontWeight: 600, color: "#333" }}>
                                {item.name}
                              </p>
                              {item.category && (
                                <p className="mb-0 text-muted" style={{ fontSize: "0.72rem" }}>
                                  {item.category}
                                </p>
                              )}
                            </div>
                            {item.quantity && (
                              <span
                                style={{
                                  background: "#fff",
                                  border: "1px solid #EEE",
                                  borderRadius: "4px",
                                  fontSize: "0.72rem",
                                  padding: "2px 8px",
                                  color: "#555",
                                  flexShrink: 0,
                                  marginLeft: "8px",
                                }}
                              >
                                {t("page.create-listing.packageDetails.items.quantity", "Qty")}: {item.quantity}
                              </span>
                            )}
                          </div>
                          {itemImages.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {itemImages.map((img, imgIdx) => (
                                <img
                                  key={imgIdx}
                                  src={getItemImageUrl(img)}
                                  alt={item.name || "Item Image"}
                                  className="rounded shadow order-image cursor-pointer mr-2 mb-1"
                                  style={{ width: "80px", height: "80px", objectFit: "cover" }}
                                  onClick={() => zoomImage(getItemImageUrl(img))}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : packageInfo.itemsDetails ? (
                <p className="heading-sm mb-0">
                  <strong>{t("page.orders.packageDetails.itemDetails", "Item Details")}:</strong>{" "}
                  {packageInfo.itemsDetails}
                </p>
              ) : null}

              {packageInfo.otherDetails && (
                <p className="heading-sm mb-0 mt-1">
                  <strong>{t("page.orders.packageDetails.otherDetails", "Other Details")}:</strong>{" "}
                  {packageInfo.otherDetails}
                </p>
              )}

              {packageInfo.packageImages?.length > 0 && (
                <div className="d-flex flex-column gap-3 mt-2">
                  <p className="mb-1 heading-sm">
                    <strong>{t("page.orders.packageDetails.packageImages", "Package Images")}:</strong>
                  </p>
                  {packageInfo.packageImages.map((img, index) => (
                    <img
                      key={index}
                      src={getPackageImageUrl(img)}
                      alt={`${t("page.orders.packageDetails.packageImages", "Package Images")} ${index + 1}`}
                      onClick={() => zoomImage(getPackageImageUrl(img))}
                      className="rounded shadow mt-1 order-image cursor-pointer"
                    />
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md="6" className="d-flex flex-column">
          <Card className="card-profile shadow border-0 mb-3 flex-grow-1">
            <CardBody className="py-2 px-3 h-100">
              <h4 className="heading-md text-muted mb-2">
                {t("page.create-listing.baggageDetails.handoverPreference", "Handover Preference")}
              </h4>

              <p className="heading-sm mb-0">
                <strong>{t("page.create-listing.baggageDetails.handoverPreference", "Preference")}:</strong>{" "}
                {pickup.handoverPreference || t("page.orders.packageDetails.noItems", "N/A")}
              </p>

              {pickup.meetingPointHint && (
                <p className="heading-sm mb-0 mt-1">
                  <strong>{t("page.create-listing.baggageDetails.meetingPointHint", "Meeting Point")}:</strong>{" "}
                  {pickup.meetingPointHint}
                </p>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={modal} toggle={toggleModal} centered size="lg">
        <ViewUploadedID
          idUrl={idUrl}
          isImage={true}
          isOpen={modal}
          toggle={toggleModal}
          isIdImage
        />
      </Modal>
    </>
  );
};

export default SenderDetails;
